<template>
  <div class="twcs-bg-white twcs-border-b twcs-border-t twcs-border-gray-200">
    <ul
      tabindex="0"
      ref="listboxModal"
      role="listbox"
      aria-labelledby="slide-over-heading"
      class="twcs-divide-y twcs-divide-gray-200 twcs-border-2 twcs-border-transparent focus-visible:twcs-outline-none focus-visible:twcs-border-2 focus-visible:twcs-border-kctcs-gold-200"
      :aria-multiselectable="panels[currentPanelIndex].multiselect"
      :aria-activedescendant="activeDescendant"
      @keydown.enter.prevent="onOptionSelect()"
      @keydown.space.prevent="onOptionSelect()"
      @keydown.arrow-down.prevent="onArrowDown()"
      @keydown.arrow-up.prevent="onArrowUp()"
      @keydown.arrow-left.stop.prevent="onArrowLeft()"
      @keydown.arrow-right.prevent="onOptionSelect()"
    >
      <li
        v-for="(option, index) in options"
        :key="option.value"
        role="option"
        :id="`${panels[currentPanelIndex].id}-modal-item-${index}`"
        @click="onOptionSelect()"
        @mouseenter="selectedIndex = index"
        @mouseleave="selectedIndex = null"
        :class="{ 'twcs-text-white twcs-bg-kctcs-navy-600': selectedIndex === index,
                  'twcs-text-gray-900': selectedIndex !== index }"
        class="twcs-cursor-default twcs-select-none twcs-relative twcs-w-full twcs-py-2 twcs-pl-8 twcs-pr-4"
        :aria-selected="panels[currentPanelIndex].select && selected[index]"
      >
        <!-- Selected: "font-semibold", Not Selected: "font-normal" -->
        <div :class="[panels[currentPanelIndex].id === 'base' ? 'twcs-flex twcs-justify-between' : '']">
          <span
            v-if="panels[currentPanelIndex].select && panels[currentPanelIndex].id !== 'base'"
            :class="{ 'twcs-font-semibold': selected[index],
                      'twcs-font-normal': !selected[index] } "
            class="twcs-block twcs-truncate twcs-w-full"
          >
            {{ option.text }}
          </span>
          <span v-else class="twcs-block twcs-truncate twcs-w-full twcs-font-normal">
            {{ option.text }}
          </span>
          <span v-if="panels[currentPanelIndex].id === 'base'">
            <span
              v-if="filtersSelectedOption(option.value).length > 0"
              :class="[selectedIndex === index ? 'twcs-text-gray-900 twcs-bg-white' : 'twcs-bg-gray-200', 'twcs-ml-auto twcs-inline-block twcs-py-0.5 twcs-px-3 twcs-text-base twcs-font-medium twcs-rounded-full']"
            >
              <span class="twcs-sr-only">Number of filters: </span>
              {{ filtersSelectedOption(option.value).length }}
            </span>
          </span>
        </div>
        <!--
          Checkmark, only display for selected option.

          Highlighted: "text-white", Not Highlighted: "text-kctcs-navy-600"
        -->
        <span
          v-if="panels[currentPanelIndex].select && panels[currentPanelIndex].id !== 'base' && selected[index]"
          :class="{ 'text-white': selectedIndex === index,
                    'text-kctcs-navy-600': selectedIndex !== index }"
          class="twcs-absolute twcs-inset-y-0 twcs-left-0 twcs-flex twcs-items-center twcs-pl-1.5"
        >
          <!-- Heroicon name: check -->
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { watch, ref, nextTick, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { usePanelStateStore } from '@/stores/panel-state.js';

let props = defineProps({
	options: { type: Array },
	selected: { type: Array },
	hasFocus: { type: Boolean, default: false },
	filtersSelected: { type: Array, default: () => [] },
});

let emit = defineEmits(['optionSelect', 'panelBack']);

const panelState = usePanelStateStore();

const { panels, currentPanelIndex } = storeToRefs(panelState);
const { updatePanelState, updateCurrentPanel } = panelState;

let isMounted = ref(false);

let selectedIndex = ref(panels.value[currentPanelIndex.value].currentIndex); // hover/focused element
let listboxModal = ref(null); // ul element
let activeDescendant = ref(`${panels.value[currentPanelIndex.value].id}-modal-item-${selectedIndex.value}`); // active active descendant of ul element

watch(() => props.hasFocus, (hasFocus) => {
	if(isMounted.value && hasFocus) {
		listboxModal.value.focus();
        setActiveDescendant();
        listboxModal.value.children[selectedIndex.value === null ? 0 : selectedIndex.value].scrollIntoView({ block: 'nearest' });
	}
});

onMounted(() => {
    isMounted.value = true;
    listboxModal.value.focus();
    setActiveDescendant();
    listboxModal.value.children[selectedIndex.value === null ? 0 : selectedIndex.value].scrollIntoView({ block: 'nearest' });
});

function setActiveDescendant() {
    activeDescendant.value = `${panels.value[currentPanelIndex.value].id}-modal-item-${selectedIndex.value}`;
}

function onArrowUp() {
	selectedIndex.value = selectedIndex.value - 1 < 0 ? props.options.length - 1 : selectedIndex.value - 1;
    setActiveDescendant();
    if (isMounted.value) {
        listboxModal.value.children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
    }
}

function onArrowDown() {
	selectedIndex.value = selectedIndex.value + 1 > props.options.length - 1 ? 0 : selectedIndex.value + 1;
    setActiveDescendant();
    if (isMounted.value) {
	    listboxModal.value.children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
    }
}

function onArrowLeft() {
    updatePanelState({ currentIndex: selectedIndex.value });

    if (panels.value[currentPanelIndex.value].select) {
        updateCurrentPanel(panels.value[currentPanelIndex.value].parentPanelId);
        emit('panelBack');
        nextTick(() => {
            selectedIndex.value = panels.value[currentPanelIndex.value].currentIndex;
            setActiveDescendant();
            listboxModal.value.children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        });
    }
}

function onOptionSelect() {
    updatePanelState({ currentIndex: selectedIndex.value });

    if (panels.value[currentPanelIndex.value].select) {
        emit('optionSelect', selectedIndex.value);
    } else {
        // navigate to sub-panel
        updateCurrentPanel(props.options[selectedIndex.value].value);
    }
}

function filtersSelectedOption(option) {
	return props.filtersSelected.filter((filter) => filter.type === option);
}
</script>
